/** One day of the creator's activity series, as the API returns it. */
export type DailyPoint = { date: string; views: number; completions: number; enrollments: number };

/**
 * Pad a daily series out to `days` entries ending today, filling the gaps with
 * zeros.
 *
 * The API returns only days that HAD activity — a deliberate choice on that
 * side, with a comment saying the SPA fills the zeros. Nothing ever did, so a
 * "last 30 days" chart with four busy days rendered four full-width bars and
 * read as a month of solid traffic. Filling here keeps the shape honest without
 * making the endpoint carry 30 rows of nothing.
 *
 * Dates are compared as the `YYYY-MM-DD` strings the API already produces, in
 * the browser's own timezone — the chart is a shape, not an accounting record.
 */
export function zeroFillDays(points: DailyPoint[], days: number, now = new Date()): DailyPoint[] {
  const byDate = new Map(points.map((p) => [p.date, p]));
  const out: DailyPoint[] = [];
  const cursor = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  cursor.setDate(cursor.getDate() - (days - 1));
  for (let i = 0; i < days; i++) {
    const date = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}-${String(
      cursor.getDate(),
    ).padStart(2, '0')}`;
    out.push(byDate.get(date) ?? { date, views: 0, completions: 0, enrollments: 0 });
    cursor.setDate(cursor.getDate() + 1);
  }
  return out;
}

/** Sum one column of a series — the chart's total line and the delta chips. */
export function sumSeries(points: DailyPoint[], key: 'views' | 'completions' | 'enrollments') {
  return points.reduce((total, p) => total + (p[key] ?? 0), 0);
}
